import m from "mithril";
import Requester from "../../shared/request";
import Gateway from "../../shared/app_settings";
import ModalManager from "../../shared/modal";
import Toasts from "../../shared/toasts";
import i18n from "../../shared/i18n/i18n";
import TextUtilities from "../../shared/utilities/text_utilities";

let saving = false;
let course, callback, days, modalId, errorMessage = "";
let daysArray = [null, null, null];
let upperBound, upperErrorKey;

const MAX_DAYS = 1999999;

/* -------------------------
   Helpers
-------------------------- */
function hasEmptySlot() {
    return daysArray.includes(null);
}

/* -------------------------
   Input handling
-------------------------- */
function handleDaysInput(e) {
    const inputValue = e.target.value;

    if (inputValue === "" || inputValue === null) {
        days = null;
        errorMessage = "";
        return;
    }

    let val = parseInt(inputValue, 10);

    if (upperBound !== null && val >= upperBound) {
        errorMessage = i18n.t(upperErrorKey) + " (" + (upperBound - 1) + ")";
    }

    else if (val > MAX_DAYS) {
        errorMessage = i18n.t("max_days_error");
        val = MAX_DAYS;
    } else {
        errorMessage = "";
    }

    days = val;
}

/* -------------------------
   Add day to first empty slot
-------------------------- */
function addDayToArray() {
    if (errorMessage || days == null || days === "") {
        return;
    }

    if (daysArray.includes(days)) {
        errorMessage = i18n.t("duplicate_reminder_days");
        return;
    }

    const index = daysArray.findIndex(d => d === null);
    if (index === -1) {
        Toasts.add("error", i18n.t("error"), i18n.t("max_three_reminders"));
        return;
    }

    daysArray[index] = days;

    // High → Low → Null
    daysArray.sort((a, b) => {
        if (a === null) return 1;
        if (b === null) return -1;
        return b - a;
    });

    days = null;
}


/* -------------------------
   Remove day
-------------------------- */
function removeDay(index) {
    daysArray[index] = null;
    // High → Low → Null
    daysArray.sort((a, b) => {
        if (a === null) return 1;
        if (b === null) return -1;
        return b - a;
    });
}

function arrayCount() {
    const count = daysArray ? daysArray.filter(v => v != null).length : 0;
    return count;
}

/* -------------------------
   Save
-------------------------- */
function saveDays() {
    if (errorMessage) {
        return;
    }

    saving = true;

    Requester.post(
        Gateway.admin +
        "courses/" +
        course.id +
        "/self-enrolment-expiry-reminder-days",
        { days: daysArray }
    )
        .then(function (courseResponse) {
            ModalManager.close(modalId);
            Toasts.add("success", i18n.t("saved_changes"), "");
            course = courseResponse;
            daysArray = [
                course.selfEnrolmentExpiryFirstReminder ?? null,
                course.selfEnrolmentExpirySecondReminder ?? null,
                course.selfEnrolmentExpiryThirdReminder ?? null
            ];
            if (callback) {
                callback(courseResponse);
            }
        })
        .catch(function (error) {
            Toasts.add(
                "error",
                i18n.t("error"),
                i18n.t(error.response?.properties?.errorCode || "unknown_error")
            );
        })
        .finally(function () {
            saving = false;
            m.redraw();
        });
}

/* -------------------------
   Display helpers
-------------------------- */
function formatDay(days) {
    return days + " " + (days === 1 ? i18n.t("day_before_expiry") : i18n.t("days_before_expiry"));
   // return i18n.t("number_of_days_format", { count: value });
}

/* -------------------------
   Modal
-------------------------- */
const Modal_Reminder_Edit = {
    oninit: function (vnode) {
        course = vnode.attrs.course;
        callback = vnode.attrs.callback;
        upperBound = vnode.attrs.upperBound ?? null;
        upperErrorKey = vnode.attrs.upperErrorKey ?? "reminder_exceeds_expiry";


        errorMessage = "";
        days = null;

        daysArray = [
            course.selfEnrolmentExpiryFirstReminder ?? null,
            course.selfEnrolmentExpirySecondReminder ?? null,
            course.selfEnrolmentExpiryThirdReminder ?? null
        ];
    },

    view: function () {
        return [
            m(".form bordered-sections", [
                m(".form-section", [
                    m(".form-label mt-2", i18n.t("set_reminder_message")),
                    m(".form-label mt-2", i18n.t("reminder_info_message_3"))]),
                    m(".form-section", [
                        m(".form-label mt-2",
                            i18n.t("number_of_days_before_expiry")
                        ),
                    m(".flex-row align-center", [

                        m("input[type=number]", {
                            min: 0,
                            max: MAX_DAYS,
                            value: days ?? "",
                            placeholder: hasEmptySlot() ? i18n.t("enter_days"): i18n.t("to_edit_modify_selected_days"),
                            oninput: handleDaysInput,
                            disabled: !hasEmptySlot()
                        }),

                        hasEmptySlot() &&
                        m("button.ml-2", { onclick: addDayToArray },
                            i18n.t("add")
                        )
                    ]),

                    errorMessage &&
                    m("div.text-danger text-sm mt-1", errorMessage),
                    ]),
                m(".form-section", [
                    m("div.form-group",
                        arrayCount() >1 ?
                            TextUtilities.formatI18nMessage("reminder_info_message",[arrayCount()]) :
                            TextUtilities.formatI18nMessage("reminder_info_message_singular",[arrayCount()])
                        ),
                    m(".mt-2", daysArray.map((d, index) =>
                        m(".flex-row align-center mb-1", [
                            m("div.text-sm",
                                [d == null ? "" :
                                    m(".btn", [
                                        formatDay(d),
                                        d !== null &&
                                    m("i.icon-x c-hand m-1", {
                                        onclick: () => removeDay(index)
                                    })]),

                                ]
                            )
                          ,

                        ])
                    )),

                    m(".label-circle-message label-warning mt-2", [
                        m("div", [
                            m("strong", i18n.t("Note") + ": "),
                            i18n.t("reminder_warning_message")
                        ])
                    ])
                ]),

                m(".form-buttons", [
                    saving
                        ? m("button.btn-text no-click", [
                            i18n.t("saving"),
                            m(".loading-dots")
                        ])
                        : m("button", { onclick: saveDays },
                            i18n.t("save")
                        )
                ])
            ])
        ];
    },

    setModalId: function (id) {
        modalId = id;
    }
};

export default Modal_Reminder_Edit;
